import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { RotateCcw, Trash2, Loader2, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");
const ALL = "__all__";

export default function RecycleBin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [area, setArea] = useState(ALL);
  const [rtype, setRtype] = useState(ALL);
  const [deletedBy, setDeletedBy] = useState(ALL);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [restoring, setRestoring] = useState(null);
  const [permFor, setPermFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems(await api.recycleBin()); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const areas = [...new Set(items.map((i) => i.area))];
  const rtypes = [...new Set(items.map((i) => i.record_type))];
  const users = [...new Set(items.map((i) => i.deleted_by).filter(Boolean))];

  const filtered = items.filter((i) => {
    if (search && !(`${i.record_name} ${i.deletion_reason}`.toLowerCase().includes(search.toLowerCase()))) return false;
    if (area !== ALL && i.area !== area) return false;
    if (rtype !== ALL && i.record_type !== rtype) return false;
    if (deletedBy !== ALL && i.deleted_by !== deletedBy) return false;
    if (dateFrom && (i.deleted_at || "") < dateFrom) return false;
    if (dateTo && (i.deleted_at || "") > dateTo + "T23:59:59") return false;
    return true;
  });

  const restore = async (item) => {
    setRestoring(item.id);
    try {
      const res = await api.restoreRecord(item.collection, item.id);
      if (res.warnings?.length) toast.warning(`Restored with warnings: ${res.warnings.join("; ")}`);
      else toast.success(`${item.record_name} restored`);
      await load();
    } catch (e) {
      const d = e.response?.data?.detail; if (typeof d === "string") toast.error(d);
    } finally { setRestoring(null); }
  };

  return (
    <>
      <PageHeader eyebrow="Administration" title="Recycle Bin" description="Records here have been soft deleted. Restore them or permanently delete (irreversible) from this screen only." />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Input data-testid="recycle-search" placeholder="Search name or reason…" value={search} onChange={(e) => setSearch(e.target.value)} className="h-9 w-full max-w-xs" />
        <Filter testid="recycle-filter-area" value={area} onChange={setArea} label="Area" options={areas} />
        <Filter testid="recycle-filter-type" value={rtype} onChange={setRtype} label="Type" options={rtypes} />
        <Filter testid="recycle-filter-user" value={deletedBy} onChange={setDeletedBy} label="Deleted by" options={users} />
        <Input type="date" data-testid="recycle-date-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-[150px]" />
        <Input type="date" data-testid="recycle-date-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-[150px]" />
      </div>

      <div data-testid="recycle-table" className="overflow-hidden rounded-xl border border-[#E4E9F3] bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-sm">
            <thead>
              <tr className="border-b border-[#E4E9F3] bg-[#F5F8FF] text-left text-xs uppercase tracking-wider text-[#5A6B8C]">
                <th className="px-4 py-3 font-medium">Record</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Area</th>
                <th className="px-4 py-3 font-medium">Deleted by</th>
                <th className="px-4 py-3 font-medium">Deleted</th>
                <th className="px-4 py-3 font-medium">Reason</th>
                <th className="px-4 py-3 text-center font-medium">Linked</th>
                <th className="px-4 py-3 text-right font-medium">Actions</th></tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="py-12 text-center"><Loader2 className="inline h-5 w-5 animate-spin text-[#1D4ED8]" /></td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={8} className="py-12 text-center text-[#5A6B8C]">The Recycle Bin is empty.</td></tr>
              ) : filtered.map((i) => (
                <tr key={`${i.collection}-${i.id}`} data-testid={`recycle-row-${i.id}`} className="border-b border-[#EEF2FB]">
                  <td className="px-4 py-3 font-medium text-[#14245F]">{i.record_name}</td>
                  <td className="px-4 py-3"><Badge variant="outline" className="border-[#DCE3F2] bg-[#EEF2FB] text-[#5A6B8C]">{i.record_type}</Badge></td>
                  <td className="px-4 py-3 text-xs text-[#5A6B8C]">{i.area}</td>
                  <td className="px-4 py-3 text-sm">{i.deleted_by || "—"}</td>
                  <td className="px-4 py-3 text-xs text-[#5A6B8C]">{fmt(i.deleted_at)}</td>
                  <td className="px-4 py-3 text-xs text-[#5A6B8C]">{i.deletion_reason || "—"}</td>
                  <td className="px-4 py-3 text-center">{i.linked_children > 0 ? <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">{i.linked_children}</Badge> : "0"}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      <Button data-testid={`restore-${i.id}`} size="sm" variant="outline" className="h-8 gap-1.5 border-[#0EA5A4]/40 text-[#0F766E] hover:bg-[#0EA5A4]/10" disabled={restoring === i.id} onClick={() => restore(i)}>
                        {restoring === i.id ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />} Restore
                      </Button>
                      <Button data-testid={`perm-delete-${i.id}`} size="sm" variant="ghost" className="h-8 gap-1.5 text-[#B91C1C] hover:bg-[#DC2626]/10" onClick={() => setPermFor(i)}>
                        <Trash2 size={14} /> Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <PermanentDeleteDialog item={permFor} onClose={() => setPermFor(null)} onDone={load} />
    </>
  );
}

function PermanentDeleteDialog({ item, onClose, onDone }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (item) setText(""); }, [item]);

  const submit = async () => {
    setBusy(true);
    try {
      const res = await api.permanentDelete(item.collection, item.id, text);
      toast.success(`${item.record_name} permanently deleted (safety backup created)`);
      onClose(); onDone();
    } catch (e) {
      const d = e.response?.data?.detail; if (typeof d === "string") toast.error(d);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="permanent-delete-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700"><AlertTriangle size={18} /> Permanently delete</DialogTitle>
          <DialogDescription>
            This will permanently remove <b>{item?.record_name}</b> ({item?.record_type}). A full safety backup is created first, but the record itself cannot be recovered.
            {item?.linked_children > 0 && <span className="mt-2 block text-amber-700">This record has {item.linked_children} linked child record(s). They will remain in the database but detached.</span>}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <p className="text-sm text-[#5A6B8C]">Type <b>DELETE</b> to confirm.</p>
          <Input data-testid="perm-delete-confirm-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="DELETE" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="perm-delete-confirm" onClick={submit} disabled={busy || text !== "DELETE"} className="bg-red-600 hover:bg-red-700">
            {busy ? <Loader2 size={16} className="animate-spin" /> : "Permanently delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Filter = ({ testid, value, onChange, label, options }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid} className="h-9 w-[150px]"><SelectValue placeholder={label} /></SelectTrigger>
    <SelectContent>
      <SelectItem value="__all__">All {label.toLowerCase()}</SelectItem>
      {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
    </SelectContent>
  </Select>
);
